import { TimezonesList } from "@/common/timezones";
import { ErrorResult, fetcher, KnownFetchResult } from "./fetcher";

export function isMac(): boolean {
	return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
}

export function isSafari(): boolean {
	return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function isMobile(): boolean {
	if (typeof window !== 'undefined') {
		if (
			window.navigator.userAgent.match(/Android/i) ||
			window.navigator.userAgent.match(/webOS/i) ||
			window.navigator.userAgent.match(/iPhone/i) ||
			window.navigator.userAgent.match(/iPad/i) ||
			window.navigator.userAgent.match(/iPod/i) ||
			window.navigator.userAgent.match(/BlackBerry/i) ||
			window.navigator.userAgent.match(/Windows Phone/i)
		) {
			return true;
		} else {
			return false;
		}
	}
	return false;
};

export function openAndFocusWindow(url: string): void {
	if (isSafari()) {
		window.location.assign(url);
	} else {
		window.open(url, '_blank')!.focus();
	}
}

const KEY_MODIFIERS = ['ctrl', 'alt', 'shift'];

export function keyForDisplay(key: string): string {
	const lowerKey = key.trim().toLowerCase();

	if (KEY_MODIFIERS.includes(lowerKey)) {
		return lowerKey.toUpperCase();
	}

	if (lowerKey.length > 1) {
		return lowerKey.substring(0, 1).toUpperCase() + lowerKey.substring(1);
	}

	return key.trim();
}

export function truncateString(val: string, count: number = 20): string {
	if (val.length > count) {
		return val.slice(0, count - 3) + "...";
	}

	return val;
}

export function scalarArrayCompare<T>(arr1: Array<T>, arr2: Array<T>): boolean {
	/**
	 *	Expects arrays to only have unique values. Shallow.
	 */
	return !!(
		arr1.length === arr2.length
		&& arr1.every((value: T) => arr2.includes(value))
	);
}

export function kbShortcutStringToComponents(
	shortcut: string,
	localize: boolean = true,	// Converts Mod to system rep
	forDisplay: boolean = false // Make the output pretty
): Array<string> {
	const result: Array<string> = [];

	const components = shortcut.split('+');

	for (let component of components) {
		component = component.trim();

		if (component.length === 0) {
			continue;
		}

		if (localize && component.toLowerCase() === 'mod') {
			let ctrl = (isMac()) ? '⌘' : 'ctrl';

			if (forDisplay) {
				ctrl = keyForDisplay(ctrl);
			}
			result.push(ctrl);
		} else if (component.toLowerCase() === 'enter') {
			result.push('⏎');
		} else if (component.toLowerCase() === 'delete') {
			result.push('del');
		} else {
			if (forDisplay) {
				component = keyForDisplay(component);
			}
			result.push(component);
		}
	}

	return result;
}

export function copyTextToClipboard(text: string): Promise<any> {
	const data = [
		//@ts-ignore
		new ClipboardItem(
			{
				"text/plain": new Blob(
					[text],
					{ type: "text/plain" }
				)
			}
		)
	];

	//@ts-ignore
	return window.navigator.clipboard.write(data);
}

// https://github.com/ueberdosis/tiptap/blob/develop/packages/core/src/utilities/isRegExp.ts#L1
export function isRegExp(value: any): value is RegExp {
	return Object.prototype.toString.call(value) === '[object RegExp]';
}

/**
 * https://github.com/ueberdosis/tiptap/blob/develop/packages/core/src/utilities/objectIncludes.ts#L8
 * Check if object1 includes object2
 * @param object1 Object
 * @param object2 Object
 */
export function objectIncludes(
	object1: Record<string, any>,
	object2: Record<string, any>,
	options: { strict: boolean } = { strict: true },
): boolean {
	const keys = Object.keys(object2);

	if (!keys.length) {
		return true;
	}

	return keys.every(key => {
		if (options.strict) {
			return object2[key] === object1[key];
		}

		if (isRegExp(object2[key])) {
			return object2[key].test(object1[key]);
		}

		return object2[key] === object1[key];
	})
}

const VALID_SUBDOMAIN_CHARSET = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789-_';

/**
 * Takes a string and makes it safe for use
 * as a subdomain
 */
export function stringToSubdomain(input: string): string {
	input = input.replace(/ /g, '-');
	input = input.toLowerCase();
	input = input.trim();

	let result = '';
	for (let c of input) {
		if (VALID_SUBDOMAIN_CHARSET.includes(c)) {
			result += c;
		}
	}

	return result;
}


export function indexToAlpha(index: number): string {
	return 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[index % 26];
}


/* Randomize array in-place using Durstenfeld shuffle algorithm */
export function inPlaceShuffleArray<T>(array: Array<T>): void {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
}


/**
 * 	Given a dom node, get the vue component, if there is one
*/
export function getVueInstanceFromDomNode(domNode: any): any | null {
	if (domNode.__vue__ !== undefined) {
		return domNode.__vue__;
	}

	return null;
}


export function getFirstVueInstanceInDescendants(domNode: Node): any | null {
	if (domNode.firstChild === null) {
		return null;
	}

	const inst = getVueInstanceFromDomNode(domNode.firstChild);

	if (inst) {
		return inst;
	}

	return getFirstVueInstanceInDescendants(domNode.firstChild);
}

/**
 * Used to exhaust options in a switch a statement for an enum typically
 */
export function assertUnreachable(x: never): never {
	throw new Error("Didn't expect to get here");
}


/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text: string, font: string): number {
	// re-use canvas object for better performance
	// @ts-ignore
	const canvas: HTMLCanvasElement = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
	const context = canvas.getContext("2d");

	if (!context) {
		return 100;
	}

	context.font = font;
	const metrics = context.measureText(text);
	return metrics.width;
}


function getCssStyle(element: HTMLElement, prop: string): string {
	return window.getComputedStyle(element, null).getPropertyValue(prop);
}


export function getElementFont(el: HTMLElement = document.body): string {
	const fontWeight = getCssStyle(el, 'font-weight') || 'normal';
	const fontSize = getCssStyle(el, 'font-size') || '16px';
	const fontFamily = getCssStyle(el, 'font-family') || 'Times New Roman';

	return `${fontWeight} ${fontSize} ${fontFamily}`;
}


export function getTimezoneChoices(): Array<any> { //DropdownChoice<string>> {
	return TimezonesList.map((tz: string) => { return { label: tz.replace(/\_/g, ' '), value: tz }; });
}


/**
 * Regex check for validating a url
 *
 * See: https://stackoverflow.com/a/14314836/632856
 * */
export function isURL(urlStr: string, requireProtocol: boolean=true): boolean {
	let urlRegex = '^(?!mailto:)(?:(?:http|https|ftp)://)(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$';
	if (!requireProtocol) {
		urlRegex = '^(?!mailto:)(?:(?:http|https|ftp)://)?(?:\\S+(?::\\S*)?@)?(?:(?:(?:[1-9]\\d?|1\\d\\d|2[01]\\d|22[0-3])(?:\\.(?:1?\\d{1,2}|2[0-4]\\d|25[0-5])){2}(?:\\.(?:[0-9]\\d?|1\\d\\d|2[0-4]\\d|25[0-4]))|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)(?:\\.(?:[a-z\\u00a1-\\uffff0-9]+-?)*[a-z\\u00a1-\\uffff0-9]+)*(?:\\.(?:[a-z\\u00a1-\\uffff]{2,})))|localhost)(?::\\d{2,5})?(?:(/|\\?|#)[^\\s]*)?$';
	}
	const url = new RegExp(urlRegex, 'i');
	return urlStr.length < 2083 && url.test(urlStr);
}


export function countWords(text: string): number {
	// This mimmics the implementation in py this is important to keep the front end logic
	//	and the backend the same, a change to one, requires a change to both
	return text.split(' ').filter((n: string) => n != '').length;
}


/**
 * Converts a hex color string to it's rgb(0-255) components
 **/
export function hexToRgb(hex: string): [number, number, number] | null {
	// Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
	const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
	hex = hex.replace(shorthandRegex, function(m, r, g, b) {
	  return r + r + g + g + b + b;
	});

	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	return result ? [
	  parseInt(result[1], 16),
	  parseInt(result[2], 16),
	  parseInt(result[3], 16)
	] : null;
}


export function youtubeUrlToEmbed(urlString: string | undefined | null): string | null | undefined {
	const template = (v: string) => `https://www.youtube.com/embed/${v}`;
	if (urlString && URL.canParse(urlString)) {
		const url = new URL(urlString);
		// short URL
		if (url.hostname === 'www.youtu.be' || url.hostname === 'youtu.be') {
			return template(url.pathname.startsWith('/') ? url.pathname.substring(1) : url.pathname);
		}
		// regular URL
		const v = url.searchParams.get('v');
		if ((url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com') && v) {
			return template(v);
		}
	}
	return urlString;
}


/**
 * Wrapper for the Google recaptcha v3 utils.
 *
 * Will get a captcha token using googles `grecaptcha` (currently only loaded in auth/).
 * Then will call the provided callback with the retrieved token.
 *
 * This token would then be sent to the back end, along with the rest of the typical request,
 * where it can be checked.
 * */
export function captchaVerified(callback: ((token: string) => void)) {
	if (grecaptcha === undefined) {
		return;
	}

	grecaptcha.ready(() => {
		grecaptcha.execute('6LemBkArAAAAAOUtPt1oUB3uD_RxQYnQsIOJpCve', {
			action: 'submit'
		}).then(callback);
	});
}


export function number_to_ordinal_suffixed_string(i: number): string {
	const j = i % 10;
	const k = i % 100;

	if (j === 1 && k !== 11) {
		return i + "st";
	}

	if (j === 2 && k !== 12) {
		return i + "nd";
	}
	if (j === 3 && k !== 13) {
		return i + "rd";
	}

	return i + "th";
}
